import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface Session {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  is_completed: boolean | null;
  current_step: number | null;
}

interface PageView {
  utm_source: string | null;
}

const LiveUTMPerformance = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageviews, setPageviews] = useState<PageView[]>([]);

  const fetchData = async () => {
    const [sessData, pvData] = await Promise.all([
      fetchAllRows<Session>('quiz_sessions', 'utm_source, utm_medium, utm_campaign, is_completed, current_step', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
      fetchAllRows<PageView>('quiz_pageviews', 'utm_source', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setSessions(sessData);
    setPageviews(pvData);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-utm')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Group by utm_source
  const sources = new Map<string, { pageviews: number; started: number; completed: number }>();
  
  pageviews.forEach(pv => {
    const src = pv.utm_source || '(direct)';
    if (!sources.has(src)) sources.set(src, { pageviews: 0, started: 0, completed: 0 });
    sources.get(src)!.pageviews++;
  });

  sessions.forEach(s => {
    const src = s.utm_source || '(direct)';
    if (!sources.has(src)) sources.set(src, { pageviews: 0, started: 0, completed: 0 });
    const entry = sources.get(src)!;
    if ((s.current_step ?? 0) >= 2) entry.started++;
    if (s.is_completed) entry.completed++;
  });

  const sourceData = Array.from(sources.entries())
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.pageviews - a.pageviews);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" />
        UTM Performance
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-2 font-medium text-gray-500 text-xs">Bron</th>
              <th className="text-right py-2 px-2 font-medium text-gray-500 text-xs">PV</th>
              <th className="text-right py-2 px-2 font-medium text-gray-500 text-xs">Gestart</th>
              <th className="text-right py-2 px-2 font-medium text-gray-500 text-xs">Voltooid</th>
              <th className="text-right py-2 px-2 font-medium text-gray-500 text-xs">CVR</th>
            </tr>
          </thead>
          <tbody>
            {sourceData.map(s => {
              const cvr = s.pageviews > 0 ? Math.round((s.completed / s.pageviews) * 100) : 0;
              return (
                <tr key={s.name} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2 px-2 font-medium text-gray-700">{s.name}</td>
                  <td className="py-2 px-2 text-right text-gray-600">{s.pageviews}</td>
                  <td className="py-2 px-2 text-right text-gray-600">{s.started}</td>
                  <td className="py-2 px-2 text-right text-gray-600">{s.completed}</td>
                  <td className="py-2 px-2 text-right">
                    <span className={`font-medium ${cvr >= 20 ? 'text-emerald-600' : cvr >= 10 ? 'text-amber-600' : 'text-red-500'}`}>
                      {cvr}%
                    </span>
                  </td>
                </tr>
              );
            })}
            {sourceData.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-center text-gray-400 text-sm">Nog geen data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LiveUTMPerformance;
